import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ContainerBlockConfig } from '@front-platform/content-builder';
import { containerBlockResolveConfig } from '@front-platform/content-builder';
import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, startWith, map, of } from 'rxjs';
import { BlockListComponent } from '@core/components/block-list/block-list.component';

export const spaceDefaults = ['none', 'small', 'medium', 'large'] as const;

export type CommonContainerOptions = {
  padding?: {
    top: string;
    bottom: string;
    left: string;
    right: string;
  };
  margin?: {
    top: string;
    bottom: string;
    left: string;
    right: string;
  };
  maxWidth?: string;
  contentPosition?: 'center' | 'left' | 'right';
};

export type ContainerStructure = CommonContainerOptions &
  (
    | {
        type: 'flex';
        direction: 'row' | 'column';
        alignItems: 'start' | 'center' | 'end' | 'stretch';
        justifyContent: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
        gap?: (typeof spaceDefaults)[number] | (string & {});
        wrap: boolean;
      }
    | {
        type: 'grid' | 'subgrid';
        columns: number;
        rows?: number;
        gap?: (typeof spaceDefaults)[number] | (string & {});
        maxWith?: string;
        margin?: {
          top: string;
          bottom: string;
          left: string;
          right: string;
        };
        padding?: {
          top: string;
          bottom: string;
          left: string;
          right: string;
        };
      }
    | {
        type: 'block';
        verticalSpace?: (typeof spaceDefaults)[number] | (string & {});
        contentPosition?: 'center' | 'left' | 'right';
      }
  );

export const BreakPoints = [
  {
    name: 'mobile',
    with: 0,
  },
  {
    name: 'tablet',
    with: 768,
  },
  {
    name: 'desktop',
    with: 1024,
  },
] as const;

export type ContainerStructurePerBreakPoints = {
  options: ContainerStructure;
  breakPoint: (typeof BreakPoints)[number]['name'];
}[];

interface BlockBlockCollectionFieldData {
  block: string;
  position: number;
}

export type ContainerData = {
  blocks: BlockBlockCollectionFieldData[];
  structure: ContainerStructurePerBreakPoints;
};

/** Default layout when CB2 only provides positioned children (no structure UI yet). */
export const DEFAULT_CONTAINER_STRUCTURE: ContainerStructurePerBreakPoints = [
  {
    breakPoint: 'mobile',
    options: {
      type: 'flex',
      direction: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      wrap: false,
      gap: 'medium',
      contentPosition: 'center',
    },
  },
];

/**
 * CB1 stores `{ blocks, structure }`. CB2 preview historically projected a bare
 * `[{ block, position }]` array for `positionedBlocks` slots — normalize both.
 */
export function normalizeContainerData(raw: unknown): ContainerData | undefined {
  if (!raw) {
    return undefined;
  }

  if (Array.isArray(raw)) {
    const blocks = raw
      .map((item, position) => {
        if (typeof item === 'string') {
          return { block: item, position };
        }
        if (item && typeof item === 'object' && 'block' in item) {
          const entry = item as BlockBlockCollectionFieldData;
          return {
            block: String(entry.block),
            position: typeof entry.position === 'number' ? entry.position : position,
          };
        }
        return null;
      })
      .filter((item): item is BlockBlockCollectionFieldData => item !== null);

    return { blocks, structure: DEFAULT_CONTAINER_STRUCTURE };
  }

  if (typeof raw === 'object') {
    const data = raw as Partial<ContainerData>;
    const blocks = Array.isArray(data.blocks) ? data.blocks : [];
    const structure =
      Array.isArray(data.structure) && data.structure.length > 0
        ? data.structure
        : DEFAULT_CONTAINER_STRUCTURE;
    return { blocks, structure };
  }

  return undefined;
}

export interface ContainerBlockRuntimeConfig {
  'container_blocks'?: ContainerData | unknown;
}

export type ContainerBlockConfigInput = Partial<
  Omit<ContainerBlockConfig, keyof ContainerBlockRuntimeConfig> & ContainerBlockRuntimeConfig
>;
export type ContainerBlockResolvedConfig = Omit<
  ContainerBlockConfig,
  keyof ContainerBlockRuntimeConfig
> &
  Partial<ContainerBlockRuntimeConfig>;
export interface ContainerBlockContract extends ConfigComponentContract<
  ContainerBlockConfigInput,
  ContainerBlockResolvedConfig
> {}

@Component({
  selector: 'app-container-block',
  standalone: true,
  imports: [CommonModule, BlockListComponent],
  templateUrl: './container-block.component.html',
  styleUrl: './container-block.component.scss',
})
export class ContainerBlockComponent implements ContainerBlockContract {
  readonly config = input<ContainerBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ContainerBlockConfig,
    ContainerBlockRuntimeConfig
  >(containerBlockResolveConfig, () => ({ config: this.config() }));
  containerBlocks = computed(() =>
    normalizeContainerData(this.resolvedConfig()['container_blocks']),
  );

  private windowWidth = toSignal(
    typeof window !== 'undefined'
      ? fromEvent(window, 'resize').pipe(
          map(() => window.innerWidth),
          startWith(window.innerWidth),
        )
      : of(0),
    { initialValue: typeof window !== 'undefined' ? window.innerWidth : 0 },
  );

  currentBreakpoint = computed(() => {
    const width = this.windowWidth();
    if (width && width >= 1024) {
      return 'desktop';
    } else if (width && width >= 768) {
      return 'tablet';
    }
    return 'mobile';
  });

  private spaceMapping = {
    none: '0',
    small: '0.5rem',
    medium: '1rem',
    large: '2rem',
  };

  currentContainerStructure = computed(() => {
    const blocks = this.containerBlocks();
    const breakpoint = this.currentBreakpoint();

    if (!blocks?.structure) {
      return null;
    }

    const currentStructure = blocks.structure.find(s => s.breakPoint === breakpoint);

    if (currentStructure) {
      return currentStructure.options;
    }

    const breakpointOrder = ['mobile', 'tablet', 'desktop'];
    const currentIndex = breakpointOrder.indexOf(breakpoint);

    for (let i = currentIndex - 1; i >= 0; i--) {
      const fallbackStructure = blocks.structure.find(s => s.breakPoint === breakpointOrder[i]);
      if (fallbackStructure) {
        return fallbackStructure.options;
      }
    }

    return null;
  });

  hasBlocks = computed(() => {
    const blocks = this.containerBlocks();
    return !!(blocks?.blocks && blocks.blocks.length > 0);
  });

  paddingTop = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.padding && structure.padding.top) {
      return this.parseSpaceValue(structure.padding.top);
    }
    return 'var(--default-padding-top)';
  });

  paddingBottom = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.padding && structure.padding.bottom) {
      return this.parseSpaceValue(structure.padding.bottom);
    }
    return 'var(--default-padding-bottom)';
  });

  paddingLeft = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.padding && structure.padding.left) {
      return this.parseSpaceValue(structure.padding.left);
    }
    return 'var(--default-padding-left)';
  });

  paddingRight = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.padding && structure.padding.right) {
      return this.parseSpaceValue(structure.padding.right);
    }
    return 'var(--default-padding-right)';
  });

  marginTop = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.margin && structure.margin.top) {
      return this.parseSpaceValue(structure.margin.top);
    }
    return 'var(--default-margin-top)';
  });

  marginBottom = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.margin && structure.margin.bottom) {
      return this.parseSpaceValue(structure.margin.bottom);
    }
    return 'var(--default-margin-bottom)';
  });

  marginLeft = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.margin && structure.margin.left) {
      return this.parseSpaceValue(structure.margin.left);
    }
    return 'var(--default-margin-left)';
  });

  marginRight = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.margin && structure.margin.right) {
      return this.parseSpaceValue(structure.margin.right);
    }
    return 'var(--default-margin-right)';
  });

  gapValue = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure && 'gap' in structure && structure.gap) {
      return this.parseSpaceValue(structure.gap);
    }
    return 'var(--default-gap)';
  });

  blockVerticalSpace = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.type === 'block') {
      const blockStructure = structure as any;
      if (blockStructure.verticalSpace) {
        return this.parseSpaceValue(blockStructure.verticalSpace);
      }
    }
    return 'var(--default-vertical-space)';
  });

  flexJustifyContent = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure && 'justifyContent' in structure) {
      switch (structure.justifyContent) {
        case 'start':
          return 'flex-start';
        case 'center':
          return 'center';
        case 'end':
          return 'flex-end';
        case 'space-between':
          return 'space-between';
        case 'space-around':
          return 'space-around';
        default:
          return 'flex-start';
      }
    }
    return 'flex-start';
  });

  flexAlignItems = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure && 'alignItems' in structure) {
      switch (structure.alignItems) {
        case 'start':
          return 'flex-start';
        case 'center':
          return 'center';
        case 'end':
          return 'flex-end';
        case 'stretch':
          return 'stretch';
        default:
          return 'flex-start';
      }
    }
    return 'flex-start';
  });

  contentAlignment = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.contentPosition) {
      switch (structure.contentPosition) {
        case 'left':
          return { marginLeft: '0', marginRight: 'auto' };
        case 'center':
          return { marginLeft: 'auto', marginRight: 'auto' };
        case 'right':
          return { marginLeft: 'auto', marginRight: '0' };
        default:
          return null;
      }
    }
    return null;
  });

  maxWidth = computed(() => {
    const structure = this.currentContainerStructure();
    if (structure?.maxWidth) {
      return structure.maxWidth;
    }
    return 'var(--default-max-width)';
  });

  private parseSpaceValue(value: string): string {
    if (this.spaceMapping.hasOwnProperty(value as keyof typeof this.spaceMapping)) {
      return this.spaceMapping[value as keyof typeof this.spaceMapping];
    }
    return value;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ContainerBlockComponent, { 'keyAliases': {} });
